import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Dialog from '../../components/ui/Dialog';
import { bookingsService } from '../../services/bookings.service';
import { BookingDetail } from '../../interfaces/booking.interface';

const formatAmount = (amount: number, currency: string) =>
  `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CodeBadge = ({ valid }: { valid: boolean | null }) => {
  const { t } = useTranslation('common');
  if (valid === true) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{t('bookings_code_valid')}</span>;
  }
  if (valid === false) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">{t('bookings_code_invalid')}</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">{t('bookings_code_legacy')}</span>;
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right text-gray-900 dark:text-gray-100">{children}</span>
  </div>
);

interface Props {
  bookingId: string | null;
  onClose: () => void;
}

const BookingDetailDialog = ({ bookingId, onClose }: Props) => {
  const { t } = useTranslation('common');
  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    setBooking(null);
    setError(false);
    bookingsService.getBooking(bookingId)
      .then((b) => { if (!cancelled) setBooking(b); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [bookingId]);

  return (
    <Dialog open={!!bookingId} onClose={onClose} title={t('bookings_detail_title')}>
      {error && <p className="text-sm text-red-600">{t('bookings_load_failed')}</p>}
      {!error && !booking && <p className="text-sm text-gray-500">{t('loading')}</p>}
      {booking && (
        <div className="space-y-4">
          {/* Photo and code side by side so admin can compare them at a glance. */}
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('bookings_completion_photo')}</h4>
            {booking.completionImageUrl ? (
              <a href={booking.completionImageUrl} target="_blank" rel="noreferrer">
                <img
                  src={booking.completionImageUrl}
                  alt={t('bookings_completion_photo')}
                  className="w-full max-h-80 object-contain rounded border border-gray-200 dark:border-gray-700"
                />
              </a>
            ) : (
              <p className="text-sm text-gray-500">{t('bookings_no_completion_photo')}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-lg font-semibold tracking-wider text-gray-900 dark:text-gray-100">
                {booking.confirmationCode ?? '—'}
              </span>
              {booking.confirmationCode && <CodeBadge valid={booking.codeValid} />}
            </div>
            {booking.completionImageUrl && booking.confirmationCode && (
              <p className="text-xs text-gray-500">{t('bookings_code_check_hint')}</p>
            )}
          </section>

          <section className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <Row label={t('status')}>{booking.status.replace('_', ' ')} · {booking.paymentStatus}</Row>
            <Row label={t('bookings_listing')}>{booking.listing?.title ?? '—'}</Row>
            <Row label={t('bookings_buyer')}>{booking.buyer ? `${booking.buyer.fullName ?? '—'} (${booking.buyer.email})` : '—'}</Row>
            <Row label={t('bookings_seller_company')}>
              {booking.seller?.fullName ?? '—'}{booking.company ? ` · ${booking.company.name}` : ''}
            </Row>
            <Row label={t('bookings_dates')}>
              {format(new Date(booking.fromDate), 'dd MMM yyyy')} → {format(new Date(booking.toDate), 'dd MMM yyyy')}
              {booking.fromTime && ` ${booking.fromTime.slice(0, 5)}–${booking.toTime?.slice(0, 5) ?? ''}`}
            </Row>
            <Row label={t('bookings_guests')}>{booking.guestCount}</Row>
            <Row label={t('bookings_pickup')}>{booking.pickupLocation} · {booking.pickupTime.slice(0, 5)}</Row>
            {booking.isInstantBooking && <Row label={t('bookings_instant')}>✓</Row>}
            {booking.notes && <Row label={t('bookings_notes')}>{booking.notes}</Row>}
          </section>

          <section className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <Row label={t('bookings_deposit')}>{formatAmount(booking.depositAmount, booking.currency)} ({booking.depositPercent}%)</Row>
            <Row label={t('bookings_extras')}>{formatAmount(booking.extrasTotal + booking.extraFeeAmount, booking.currency)}</Row>
            <Row label={t('bookings_total')}><strong>{formatAmount(booking.totalAmount, booking.currency)}</strong></Row>
          </section>

          {booking.extras.length > 0 && (
            <section className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <h4 className="text-sm font-semibold mb-1 text-gray-900 dark:text-gray-100">{t('bookings_extra_items')}</h4>
              {booking.extras.map((e) => (
                <Row key={e.id} label={`${e.name} × ${e.quantity}`}>{formatAmount(e.price * e.quantity, booking.currency)}</Row>
              ))}
            </section>
          )}

          {booking.paymentLinks.length > 0 && (
            <section className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <h4 className="text-sm font-semibold mb-1 text-gray-900 dark:text-gray-100">{t('bookings_payment_links')}</h4>
              {booking.paymentLinks.map((l) => (
                <Row key={l.id} label={`${l.label}${l.description ? ` · ${l.description}` : ''}`}>
                  {formatAmount(l.amount, booking.currency)} · {l.status}
                </Row>
              ))}
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default BookingDetailDialog;
